import type { HomeData } from '@blockbattle/shared';
import type { ActivityTask } from './api.js';

export type DailyGoalState =
  | { readonly kind: 'EMPTY'; readonly completed: 0; readonly total: 0 }
  | {
      readonly kind: 'ACTIVE' | 'CLAIMABLE';
      readonly task: ActivityTask;
      readonly completed: number;
      readonly total: number;
    }
  | {
      readonly kind: 'ALL_COMPLETE';
      readonly unclaimed: readonly ActivityTask[];
      readonly completed: number;
      readonly total: number;
    };

export const selectDailyGoal = (tasks: readonly ActivityTask[]): DailyGoalState => {
  if (!tasks.length) return { kind: 'EMPTY', completed: 0, total: 0 };
  const completed = tasks.filter((task) => task.completed).length;
  if (completed === tasks.length)
    return {
      kind: 'ALL_COMPLETE',
      unclaimed: tasks.filter((task) => !task.claimed),
      completed,
      total: tasks.length,
    };
  const claimable = tasks
    .filter((task) => task.completed && !task.claimed)
    .sort((left, right) => left.reward - right.reward || left.key.localeCompare(right.key))[0];
  if (claimable) return { kind: 'CLAIMABLE', task: claimable, completed, total: tasks.length };
  const active = tasks
    .filter((task) => !task.completed)
    .sort((left, right) => {
      const leftRemaining = Math.max(0, left.target - left.progress) / Math.max(1, left.target);
      const rightRemaining = Math.max(0, right.target - right.progress) / Math.max(1, right.target);
      return (
        leftRemaining - rightRemaining ||
        left.reward - right.reward ||
        left.key.localeCompare(right.key)
      );
    })[0];
  return active
    ? { kind: 'ACTIVE', task: active, completed, total: tasks.length }
    : { kind: 'ALL_COMPLETE', unclaimed: [], completed, total: tasks.length };
};

export const selectArenaCta = (home: HomeData) => {
  const canPlay = home.energy.current >= home.energy.costDaily;
  return {
    canPlay,
    route: canPlay ? '/game/daily' : null,
    label: canPlay ? `Играть · ⚡ ${home.energy.costDaily}` : 'Восстановить энергию',
  } as const;
};

export const selectArenaCopy = (home: HomeData) => {
  if (!home.daily.bestScore || !home.rank)
    return {
      title: 'Займите место в рейтинге',
      subtitle: 'Одинаковые фигуры для всех игроков',
    };
  if (home.rank === 1)
    return { title: 'Удержите первое место', subtitle: 'Улучшите личный рекорд' };
  if (home.daily.nextRank)
    return {
      title: `Обгоните #${home.daily.nextRank.rank}`,
      subtitle: `До следующего места — ${home.daily.nextRank.gap.toLocaleString('ru-RU')} ${scoreWord(home.daily.nextRank.gap)}`,
    };
  return { title: 'Улучшите свой рекорд', subtitle: `Сейчас вы #${home.rank}` };
};

const scoreWord = (value: number): string => {
  const modulo100 = value % 100;
  const modulo10 = value % 10;
  if (modulo100 >= 11 && modulo100 <= 14) return 'очков';
  if (modulo10 === 1) return 'очко';
  if (modulo10 >= 2 && modulo10 <= 4) return 'очка';
  return 'очков';
};

export const shouldShowFeaturedCosmetic = (
  cosmetic: HomeData['featuredCosmetic'],
): cosmetic is NonNullable<HomeData['featuredCosmetic']> => cosmetic !== null;

export const motionDuration = (reducedMotion: boolean, duration: number): number =>
  reducedMotion ? 0 : duration;

export const selectRpForecast = (home: HomeData): HomeData['league']['forecast'] =>
  home.league.forecast;

export const formatCountdown = (target: string | null, now: number): string | null => {
  if (!target) return null;
  const remaining = Math.max(0, new Date(target).getTime() - now);
  const hours = Math.floor(remaining / 3_600_000);
  const minutes = Math.floor((remaining % 3_600_000) / 60_000);
  const seconds = Math.floor((remaining % 60_000) / 1_000);
  return hours > 0
    ? `${hours} ч ${String(minutes).padStart(2, '0')} мин`
    : `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const selectClubCopy = (club: HomeData['club'], now: number) => {
  if (!club) return { title: 'Найдите команду', subtitle: 'Общая цель и клубные награды' };
  const remaining = formatCountdown(club.weekEndsAt, now);
  if (club.nextClub)
    return {
      title: `До #${club.nextClub.rank} — ${club.nextClub.gap.toLocaleString('ru-RU')} оч.`,
      subtitle: `${club.name}${remaining ? ` · ${remaining}` : ''}`,
    };
  return {
    title: club.rank === 1 ? 'Клуб лидирует' : `Клуб ${club.rank ? `#${club.rank}` : 'в Кубке'}`,
    subtitle: `${club.name}${remaining ? ` · ${remaining}` : ''}`,
  };
};
